import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Clock, User, FileText } from 'lucide-react';
import { toast } from 'sonner';

export default function ActivityLogs() {
  const [logs, setLogs] = useState([]);
  const [filterModule, setFilterModule] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLogs();
  }, [filterModule]);

  const fetchLogs = async () => {
    try {
      const url = filterModule ? `/activity-logs?module=${filterModule}` : '/activity-logs';
      const res = await api.get(url);
      setLogs(res.data || []);
    } catch (error) {
      console.error('Failed to fetch logs:', error);
      toast.error('Failed to fetch activity logs');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const getActionColor = (action) => {
    switch(action) {
      case 'CREATE': return 'text-green-600 bg-green-50';
      case 'UPDATE': return 'text-blue-600 bg-blue-50';
      case 'DELETE': return 'text-red-600 bg-red-50';
      case 'LOGIN': return 'text-purple-600 bg-purple-50';
      case 'RELEASE': return 'text-orange-600 bg-orange-50';
      case 'REOPEN': return 'text-yellow-600 bg-yellow-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div className="space-y-6 fade-in" data-testid="activity-logs-page">
      <div>
        <h1 className="text-4xl font-bold text-primary mb-2">Activity Logs</h1>
        <p className="text-muted-foreground">Track all user activities for audit and compliance</p>
      </div>

      <Card className="p-6">
        <div className="mb-6">
          <Label>Filter by Module</Label>
          <Select value={filterModule || "all"} onValueChange={(value) => setFilterModule(value === "all" ? "" : value)}>
            <SelectTrigger className="max-w-xs bg-white border-2 border-primary/30">
              <SelectValue placeholder="All Modules" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Modules</SelectItem>
              <SelectItem value="Authentication">Authentication</SelectItem>
              <SelectItem value="Purchase Order">Purchase Order</SelectItem>
              <SelectItem value="Receiving">Receiving</SelectItem>
              <SelectItem value="Store Requisition">Store Requisition</SelectItem>
              <SelectItem value="Master Data">Master Data</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {logs.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground text-lg">No activity logs found</p>
          </div>
        ) : (
          <div className="space-y-3">
            {logs.map((log, idx) => (
              <div 
                key={log.id || idx} 
                className="border rounded-lg p-4 hover:bg-accent/10 transition-colors"
                data-testid={`activity-log-${idx}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${getActionColor(log.action)}`}>
                        {log.action}
                      </span>
                      <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-primary/10 text-primary">
                        {log.module}
                      </span>
                    </div>
                    
                    <p className="text-sm text-foreground mb-2">{log.description}</p>
                    
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {log.user} ({log.role})
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatTimestamp(log.timestamp)}
                      </span>
                      {log.ip_address && (
                        <span className="flex items-center gap-1">
                          🌐 {log.ip_address}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <div className="text-sm text-muted-foreground">
        <p>💡 <strong>Tip:</strong> Activity logs help prevent fraud and ensure accountability.</p>
        <p>All user actions are tracked including login, create, update, delete operations.</p>
      </div>
    </div>
  );
}
